/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import imgixLib from 'js/lib/imgix';

import { Grid, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { CardV2 } from '@coursera/coursera-ui';

import { numberOfRelatedCoursesForDomains } from 'bundles/browse/components/Domain/constants/domainData';
import { buildNewWindowOnClickViaUrl } from 'bundles/common/utils/newWindowUtils';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { getLowQualityImageSrc } from 'bundles/page/components/shared/utils/lazyImgixUtils';

import _t from 'i18n!nls/front-page';

const THUMBNAIL_COMPRESSION_PARAMS = { fit: 'crop', auto: 'format,compress', height: '100' };

const styles = {
  container: (theme: Theme) => css`
    display: block;
    flex-basis: 25%;
    padding-right: 20px;
    margin-bottom: 20px;

    &:hover {
      text-decoration: none;
    }
    ${theme.breakpoints.down('md')} {
      flex-basis: 33.3%;
    }

    ${theme.breakpoints.down('sm')} {
      flex-basis: 50%;
    }

    ${theme.breakpoints.down('xs')} {
      flex-basis: 100%;
      padding-right: 0;
    }

    .domain-card {
      background-color: white;
      height: 100%;
    }
  `,
  cardContent: css`
    align-items: center;
  `,
  thumbnail: css`
    width: 80px;
    min-width: 80px;
    height: 80px;
    margin-right: 10px;
    background-repeat: no-repeat;
    background-size: cover;
  `,
  title: css`
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    color: #1f1f1f;
  `,
  courseCount: css`
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    color: #636363;
  `,
};

type Props = {
  id: string;
  slug: string;
  name: string;
  imageUrl: string;
  isMobile: boolean;
  useLazyImage?: boolean;
};

const DomainPill = ({ id, name, slug, imageUrl, isMobile, useLazyImage }: Props) => {
  const href = `/browse/${id}`;
  const backgroundImage = useLazyImage
    ? getLowQualityImageSrc({ src: imageUrl, maxWidth: 80, ...THUMBNAIL_COMPRESSION_PARAMS, maxHeight: 80 })
    : imgixLib.processImage(imageUrl, THUMBNAIL_COMPRESSION_PARAMS);
  return (
    <TrackedA
      css={styles.container}
      href={href}
      onClick={buildNewWindowOnClickViaUrl({ href })}
      trackingName="domain_pill"
      data={{ id, name, slug, imageUrl, isMobile }}
    >
      <CardV2 rootClassName="domain-card" isInteractive>
        <Grid container alignItems="center">
          <div css={styles.thumbnail} style={{ backgroundImage: `url(${backgroundImage})` }} />
          <Grid item xs={8} data-e2e="domain-pill-label-box" direction="column">
            <Typography2 component="div" variant="bodySecondary" css={styles.title}>
              {name}
            </Typography2>
            <Typography2 component="span" color="supportText" variant="bodySecondary" css={styles.courseCount}>
              {/* @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message */}
              {_t('#{numberOfCourses} courses', { numberOfCourses: numberOfRelatedCoursesForDomains[slug || id] })}
            </Typography2>
          </Grid>
        </Grid>
      </CardV2>
    </TrackedA>
  );
};

export default DomainPill;
